import React from 'react';
import { Download, FileImage, FileText, Mail } from 'lucide-react';
import { TGSTPropertyLabels, TReportTypeCode } from '@@config/report';
import RetailerToolsDisclosure, {
    type TRetailerToolItem,
} from '@@components/RetailerToolsDisclosure/RetailerToolsDisclosure';

type TReportSupplementalProps = {
    readonly reportTypeCode: TReportTypeCode;
    readonly reportNumber: string | number;
    readonly primaryCTA?: URL | undefined /* Default: Report PDF URL */;
    readonly reportDAC: string /* Report Access Card URL */;
    readonly reportDTL: string /* Diamond Type Letter (PDF) URL */;
    readonly reportDTLP: string /* Diamond Type Letter with Photo (PDF) URL */;
    readonly reportDTTL: string /* Diamond Color Treatment Letter (PDF) URL */;
    readonly reportPCL: string /* POS Case Label (PDF) URL */;
    readonly i18n: TGSTPropertyLabels;
};

const CARD_LINK_CLASS =
    'inline-flex min-h-6 items-center gap-2 py-2 font-arial text-[12px] font-medium normal-case tracking-normal text-[color:var(--text-blue)] decoration-dotted underline-offset-4 focus-visible:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--text-blue)]';

const ReportSupplemental: React.FC<TReportSupplementalProps> = ({
    reportTypeCode,
    reportNumber,
    primaryCTA,
    reportDAC,
    reportDTL,
    reportDTLP,
    reportDTTL,
    reportPCL,
    i18n,
}) => {
    // For the email to friend link curate the email subject and body for the "mailto" href
    const mailSubject: string = i18n['gia_www_i18n_emailsubject'] ?? 'GIA.edu: View GIA Report';
    const mailBody: string = i18n['gia_www_i18n_emailbody'] ?? 'Take a look at this GIA report via Report Check:';
    const emailMailToHref: string = `mailto:?Subject=${mailSubject}: ${reportNumber}&body=${mailBody}: ${window.location.href}`;

    const retailerTools: TRetailerToolItem[] = (
        [
            {
                href: reportDAC,
                label: i18n['gia_www_i18n_report_access_card'] ?? 'Report Access Card',
                description:
                    i18n['gia_www_i18n_report_access_card_description'] ?? 'Printable card for in-store display',
                icon: 'card',
            },
            {
                href: reportPCL,
                label: i18n['gia_www_i18n_pos_case_label'] ?? 'POS Case Label',
                description: i18n['gia_www_i18n_pos_case_label_description'] ?? 'Small label for point-of-sale cases',
                icon: 'tag',
            },
        ] satisfies TRetailerToolItem[]
    ).filter(item => item.href);

    const letters = [
        {
            href: reportDTL,
            label: i18n['gia_www_i18n_diamond_type_letter'] ?? 'Diamond Type Letter',
            icon: <FileText size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />,
        },
        {
            href: reportDTLP,
            label: i18n['gia_www_i18n_diamond_type_letter_with_photo'] ?? 'Diamond Type Letter with Photo',
            icon: <FileImage size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />,
        },
        {
            href: reportDTTL,
            label: i18n['gia_www_i18n_diamond_color_treatment_letter'] ?? 'Diamond Color Treatment Letter',
            icon: <FileText size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />,
        },
    ].filter(letter => letter.href);

    return (
        <section className='text-center md:text-right'>
            <div className='w-full'>
                {primaryCTA && (
                    <a
                        href={primaryCTA.toString()}
                        className='btn btn-primary mb-2 flex w-full items-center justify-center gap-2 rounded focus-visible:!outline focus-visible:!outline-2 focus-visible:outline-offset-2 focus-visible:!outline-[color:var(--text-blue)]'
                        target='_blank'
                        rel='noreferrer'
                    >
                        <Download size={20} strokeWidth={2} className='shrink-0' aria-hidden='true' />
                        <span>
                            {reportTypeCode === 'DER'
                                ? i18n['gia_www_i18n_view_ereport']
                                : i18n['gia_www_i18n_download_report_pdf']}
                        </span>
                    </a>
                )}
                <div className='mt-2 w-full'>
                    <RetailerToolsDisclosure
                        items={retailerTools}
                        triggerLabel={i18n['gia_www_i18n_retailer_tools'] ?? 'Retailer Tools'}
                    >
                        <div className='flex flex-col items-start'>
                            <a href={emailMailToHref} className={CARD_LINK_CLASS}>
                                <Mail size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />
                                <span>{i18n['gia_www_i18n_emailreport'] ?? 'Email Report'}</span>
                            </a>
                            {letters.map(({ href, label, icon }) => (
                                <a key={href} href={href} className={CARD_LINK_CLASS} target='_blank' rel='noreferrer'>
                                    {icon}
                                    <span>{label}</span>
                                </a>
                            ))}
                        </div>
                    </RetailerToolsDisclosure>
                </div>
            </div>
        </section>
    );
};

export type { TReportSupplementalProps };
export default ReportSupplemental;
